import React from 'react';

import { PaginationPanel } from '@vlabs/uikit';

import type { EventListPermissions } from '@vlabs/monokit/entities/events/event-list-table';
import { DEFAULT_EVENT_LIST_PERMISSIONS } from '@vlabs/monokit/entities/events/event-list-table';
import type { EventListFilters } from '@vlabs/monokit/entities/events/filters';
import { useEventListFilters } from '@vlabs/monokit/entities/events/filters';

import st from './EventListTableViewer.module.sass';

interface UseEventListQueryResult<Events> {
  data?: { events?: Events[] };
}

interface EventListTableViewerProps<Events> {
  useGetEventsHook: (params: EventListFilters) => UseEventListQueryResult<Events>;
  tableView: React.ComponentType<{ data: Events[]; permissions: EventListPermissions }>;
  permissions?: EventListPermissions;
}

function EventListTableViewer<Events>({
  useGetEventsHook,
  tableView: TableView,
  permissions = DEFAULT_EVENT_LIST_PERMISSIONS,
}: EventListTableViewerProps<Events>): React.JSX.Element {
  const { filters, updateFilters } = useEventListFilters();
  const { data: events } = useGetEventsHook(filters);
  const eventsData = events?.events ?? [];

  const currentPage = filters?.page ?? 1;
  const canNextPage = eventsData.length >= (filters.page_size ?? 0);

  return (
    <React.Fragment>
      <TableView
        data={eventsData}
        permissions={permissions}
      />
      <div className={st.Pagination}>
        <PaginationPanel
          canNextPage={canNextPage}
          canPreviousPage={currentPage > 1}
          gotoPage={(page) => updateFilters({ page: page + 1 })}
          nextPage={() => updateFilters({ page: currentPage + 1 })}
          pageCount={currentPage + (canNextPage ? 1 : 0)}
          pageIndex={currentPage - 1}
          pageSize={filters.page_size}
          previousPage={() => updateFilters({ page: currentPage - 1 })}
          setPageSize={(page_size: number) => updateFilters({ page_size })}
        />
      </div>
    </React.Fragment>
  );
}

export { EventListTableViewer };
